import { makeAutoObservable, action } from "mobx";
import { MentionItem } from "../types/mention.types";

export interface SelectedToken {
  symbol: string; // e.g., "ETH", "BTC"
  mode: string; // e.g., "basic", "erc20"
  id: string; // original id/address for API calls
  label: string; // display name
  icon: string; // token icon URL
  networkCode: string; // network identifier
}

export class SelectedTokensStore {
  selectedTokens: Map<string, SelectedToken> = new Map(); // key: "symbol/mode"

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Add a token to the selected tokens store
   * @param item - The mention item selected by user
   */
  addSelectedToken = action((item: MentionItem) => {
    const key = `${item.label}/${item.source}`;
    const selectedToken: SelectedToken = {
      symbol: item.label,
      mode: item.source,
      id: item.id,
      label: item.label,
      icon: item.icon,
      networkCode: item.networkCode,
    };

    this.selectedTokens.set(key, selectedToken);
    console.log("✅ Token added to selected tokens:", key, selectedToken);
  });

  /**
   * Get a selected token by symbol/mode
   * @param symbol - Token symbol (e.g., "ETH")
   * @param mode - Token mode (e.g., "basic")
   */
  getSelectedToken(symbol: string, mode: string): SelectedToken | undefined {
    const key = `${symbol}/${mode}`;
    return this.selectedTokens.get(key);
  }

  /**
   * Check if a token is selected
   * @param symbol - Token symbol
   * @param mode - Token mode
   */
  isTokenSelected(symbol: string, mode: string): boolean {
    const key = `${symbol}/${mode}`;
    return this.selectedTokens.has(key);
  }

  /**
   * Get all selected tokens as array
   */
  getAllSelectedTokens(): SelectedToken[] {
    return Array.from(this.selectedTokens.values());
  }

  /**
   * Clear all selected tokens
   */
  clearAllTokens = action(() => {
    this.selectedTokens.clear();
    console.log("🗑️ All selected tokens cleared");
  });

  /**
   * Remove a specific token
   * @param symbol - Token symbol
   * @param mode - Token mode
   */
  removeToken = action((symbol: string, mode: string) => {
    const key = `${symbol}/${mode}`;
    const removed = this.selectedTokens.delete(key);
    if (removed) {
      console.log("🗑️ Token removed:", key);
    }
    return removed;
  });
}
